import { useEffect, useState, useCallback, Fragment } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Map, ChevronLeft, ChevronRight, Printer, Car, AlertTriangle, X, ArrowRightLeft, CheckCircle2 } from "lucide-react";

const ROUTE_TYPES = [
  { key: "lulu", label: "Lulu Trip", codes: ["LFT"] },
  { key: "short", label: "Short Route", codes: ["SR1", "SR2", "SR 3"] },
  { key: "long", label: "Long Route", codes: ["LR1", "LR2"] },
  { key: "onsite", label: "Onsite", codes: ["ONS"] },
];
function todayIST() {
  // IST = UTC + 5:30, independent of the browser's local timezone
  return new Date(Date.now() + 5.5 * 60 * 60000).toISOString().slice(0, 10);
}

export default function RouteSummary() {
  const [date, setDate] = useState(todayIST());
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState({});
  const [shortages, setShortages] = useState([]);
  const [shiftable, setShiftable] = useState([]);
  const [modal, setModal] = useState(false);
  const [sel, setSel] = useState({});
  const [checked, setChecked] = useState(false);

  const checkShortages = useCallback(async (auto) => {
    try {
      const r = await api.get(`/admin/reports/shortage-check?date=${date}`);
      setShortages(r.data.shortages || []);
      setShiftable(r.data.shiftable_customers || []);
      setSel({});
      if ((r.data.shortages || []).length > 0 && auto) setModal(true);
    } catch { /* ignore */ }
  }, [date]);

  useEffect(() => { checkShortages(!checked); setChecked(true); /* eslint-disable-next-line */ }, [date]);

  const fetchSummary = async (routeKey, d = date) => {
    setSelectedRoute(routeKey); setLoading(true); setData(null);
    try { const r = await api.get(`/admin/reports/route-summary?route_type=${routeKey}&date=${d}`); setData(r.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const changeDate = (days) => {
    const dt = new Date(date + "T12:00:00"); dt.setDate(dt.getDate() + days);
    const nd = dt.toISOString().slice(0, 10);
    setDate(nd); setChecked(false);
    if (selectedRoute) fetchSummary(selectedRoute, nd);
  };

  const shift = async (c) => {
    const nr = sel[c.customer_id];
    if (!nr) return toast.error("Select a route to shift to");
    try {
      await api.post("/admin/reports/shift-customer-route", { customer_id: c.customer_id, date, new_route_code: nr });
      toast.success(`${c.customer_name} moved to ${nr}`);
      setShiftable((p) => p.filter((x) => x.customer_id !== c.customer_id));
      if (selectedRoute) fetchSummary(selectedRoute);
      checkShortages(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const rt = ROUTE_TYPES.find((r) => r.key === selectedRoute);
  const getGroups = () => {
    if (!data || !rt) return [];
    return rt.codes.map((code) => ({
      code,
      customers: (data.customers || []).filter((c) => c.route_code === code).sort((a, b) => a.name.localeCompare(b.name)),
    }));
  };
  const groups = getGroups();
  const groupTotal = (item, g) => g.customers.reduce((s, c) => s + (data.matrix[item]?.[c.id] || 0), 0);

  const handlePrint = () => {
    if (!data || data.customers.length === 0) return toast.error("No data to print");
    const isLulu = selectedRoute === "lulu";
    const dateStr = new Date(data.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const driverLines = (rt?.codes || []).map((c) => (drivers[c] ? `${c}: ${drivers[c]}` : "")).filter(Boolean).join(" | ");
    let header = '<th class="item-hdr">Item</th>';
    groups.forEach((g) => { header += `<th class="total-hdr">${g.code}<br/>Total</th>`; g.customers.forEach((c) => { header += `<th class="cust">${c.name}</th>`; }); });
    let rows = "";
    data.items.forEach((item) => {
      let cells = `<td class="item">${item}</td>`;
      groups.forEach((g) => { cells += `<td class="total-cell">${groupTotal(item, g) || ""}</td>`; g.customers.forEach((c) => { cells += `<td class="qty">${data.matrix[item]?.[c.id] || ""}</td>`; }); });
      rows += `<tr>${cells}</tr>`;
    });
    const pageStyle = isLulu ? "@page { size: A4 portrait; margin: 8mm; }" : "@page { size: A4 landscape; margin: 6mm; }";
    const custStyle = isLulu
      ? "th.cust { min-width:50px; max-width:70px; font-size:11px; padding:4px 2px; word-wrap:break-word; }"
      : "th.cust { writing-mode: vertical-rl; text-orientation: mixed; transform: rotate(180deg); min-width:28px; max-width:40px; height:110px; font-size:11px; padding:3px 2px; }";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      ${pageStyle} * { box-sizing:border-box; margin:0; padding:0; }
      body { font-family: Arial, sans-serif; color:#000; } .hdr{font-weight:bold;font-size:22px;margin-bottom:2px;} .sub{font-size:14px;margin-bottom:4px;}
      table{border-collapse:collapse;margin-top:4px;table-layout:auto;width:100%;} th,td{border:1px solid #000;}
      th{background:#fff;color:#000;text-align:center;font-weight:bold;} th.item-hdr{text-align:left;}
      th.total-hdr{min-width:44px;border:2.5px solid #000;background:#999;color:#fff;font-size:12px;padding:3px;} ${custStyle}
      td.item{font-weight:bold;white-space:nowrap;font-size:12px;padding:2px 3px;} td.total-cell{text-align:center;font-weight:bold;background:#999;color:#000;min-width:44px;border-left:2.5px solid #000;border-right:2.5px solid #000;} td.qty{text-align:center;font-size:12px;} tr{page-break-inside:avoid;} thead{display:table-header-group;}
    </style></head><body><div class="hdr">${rt?.label || ""}</div><div class="sub">${dateStr}${driverLines ? " | Driver: " + driverLines : ""}</div>
    <table><thead><tr>${header}</tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;left:0;top:0;width:100%;height:100%;border:none;opacity:0;pointer-events:none;";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 2000); }, 500);
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-center gap-2 mb-3">
        <Map className="w-6 h-6 text-[#8B4513]" />
        <h1 className="text-xl font-bold text-[#8B4513] flex-1">Route Summaries</h1>
        {shortages.length > 0 && (
          <button onClick={() => setModal(true)} data-testid="shortage-indicator" className="flex items-center gap-1 bg-[#e65100] text-white rounded-full px-3 py-1.5">
            <AlertTriangle className="w-4 h-4" /><span className="font-bold text-sm">{shortages.length}</span>
          </button>
        )}
      </div>

      {/* Date nav */}
      <div className="flex items-center justify-center gap-3 mb-3">
        <button onClick={() => changeDate(-1)} data-testid="date-prev" className="p-2 text-[#8B4513]"><ChevronLeft className="w-6 h-6" /></button>
        <span className="font-semibold text-[#333]">{new Date(date + "T12:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</span>
        <button onClick={() => changeDate(1)} data-testid="date-next" className="p-2 text-[#8B4513]"><ChevronRight className="w-6 h-6" /></button>
      </div>

      {/* Driver inputs */}
      {rt && (
        <div className="space-y-2 mb-3">
          {rt.codes.map((code) => (
            <div key={code} className="flex items-center gap-2">
              <span className="w-24 text-sm font-bold text-[#555]">{code} Driver:</span>
              <input value={drivers[code] || ""} onChange={(e) => setDrivers({ ...drivers, [code]: e.target.value })} placeholder={`Enter ${code} driver`}
                className="flex-1 px-3 py-2 rounded-lg bg-white border border-[#e6dcc0] text-sm focus:outline-none focus:border-[#8B4513]" />
            </div>
          ))}
        </div>
      )}

      {/* Route buttons */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {ROUTE_TYPES.map((r) => (
          <button key={r.key} onClick={() => fetchSummary(r.key)} data-testid={`route-btn-${r.key}`}
            className={`rounded-xl p-3 border-2 border-[#8B4513] flex flex-col items-center ${selectedRoute === r.key ? "bg-[#8B4513] text-white" : "bg-white text-[#8B4513]"}`}>
            <Car className="w-5 h-5" />
            <span className="font-bold text-sm mt-1">{r.label}</span>
            <span className={`text-[11px] mt-0.5 ${selectedRoute === r.key ? "text-[#ddd]" : "text-[#999]"}`}>{r.codes.join(", ")}</span>
          </button>
        ))}
      </div>

      {loading ? <div className="h-40 rounded-xl skeleton" /> : data ? (
        data.customers.length === 0 ? <p className="text-center text-[#999] mt-6">No orders found for {rt?.label} on this date.</p> : (
          <>
            <p className="text-sm font-semibold text-[#555] mb-2">{rt?.label} — {data.customers.length} customers, {data.items.length} items</p>
            <div className="overflow-x-auto border border-[#ddd] rounded-lg" data-testid="route-table">
              <table className="border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-[#f5f0ea] border border-[#ddd] p-1.5 text-left min-w-[130px]">Item</th>
                    {groups.map((g) => (
                      <Fragment key={g.code}>
                        <th className="bg-[#ddd] border-2 border-[#333] p-1.5 min-w-[44px]">{g.code}<br />Total</th>
                        {g.customers.map((c) => <th key={c.id} className="bg-[#555] text-white border border-[#ddd] p-1.5 min-w-[56px] max-w-[70px] leading-tight">{c.name}</th>)}
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, idx) => (
                    <tr key={item} className={idx % 2 ? "bg-[#faf8f5]" : ""}>
                      <td className="sticky left-0 bg-[#f5f0ea] border border-[#ddd] p-1.5 font-semibold whitespace-nowrap">{item}</td>
                      {groups.map((g) => (
                        <Fragment key={g.code}>
                          <td className="bg-[#f0ece6] border-2 border-[#333] p-1.5 text-center font-bold">{groupTotal(item, g) || ""}</td>
                          {g.customers.map((c) => <td key={c.id} className="border border-[#ddd] p-1.5 text-center font-bold">{data.matrix[item]?.[c.id] || ""}</td>)}
                        </Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={handlePrint} data-testid="route-print-btn" className="w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#8B4513] hover:bg-[#703710] text-white font-bold">
              <Printer className="w-5 h-5" /> Print / Save PDF
            </button>
          </>
        )
      ) : <p className="text-center text-[#999] mt-6">Select a route above to view the summary.</p>}

      {/* Shortage & shift modal */}
      {modal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="shortage-modal">
            <div className="flex items-center justify-between p-4 border-b border-[#eee]">
              <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#e65100]" /><span className="font-bold text-[#333]">Shortage & Route Shift</span></div>
              <button onClick={() => setModal(false)} data-testid="shortage-close"><X className="w-6 h-6 text-[#666]" /></button>
            </div>
            <div className="p-4 overflow-y-auto">
              <h3 className="text-sm font-bold text-[#333] mb-2">Shortage Warnings ({shortages.length} items)</h3>
              {shortages.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-[#e8f5e9] rounded-lg text-[#2e7d32] font-semibold"><CheckCircle2 className="w-5 h-5" /> No shortages detected</div>
              ) : (
                <div className="border border-[#ddd] rounded-lg overflow-hidden text-xs mb-2">
                  <div className="flex bg-[#f5f5f5] font-bold text-[#555]">
                    <div className="flex-[3] p-2">Item</div><div className="flex-1 p-2 text-center">Stock</div><div className="flex-1 p-2 text-center">LFT</div><div className="flex-1 p-2 text-center">SR1</div><div className="flex-1 p-2 text-center">LR1</div><div className="flex-1 p-2 text-center">Total</div><div className="flex-1 p-2 text-center text-[#d32f2f]">Short</div>
                  </div>
                  {shortages.map((s, i) => (
                    <div key={i} className={`flex border-t border-[#eee] ${i % 2 ? "bg-[#fafafa]" : ""}`} data-testid={`shortage-item-${i}`}>
                      <div className="flex-[3] p-2 font-semibold">{s.item}</div><div className="flex-1 p-2 text-center text-[#2e7d32]">{s.stock}</div>
                      <div className="flex-1 p-2 text-center">{s.demand_lft || "-"}</div><div className="flex-1 p-2 text-center">{s.demand_sr1 || "-"}</div><div className="flex-1 p-2 text-center">{s.demand_lr1 || "-"}</div>
                      <div className="flex-1 p-2 text-center font-bold">{s.total_demand}</div><div className="flex-1 p-2 text-center font-bold text-[#d32f2f]">-{s.short_by}</div>
                    </div>
                  ))}
                </div>
              )}
              <h3 className="text-sm font-bold text-[#333] mt-4 mb-1 flex items-center gap-1"><ArrowRightLeft className="w-4 h-4 text-[#1565c0]" /> Shift Customers to Later Route</h3>
              <p className="text-xs text-[#777] mb-3">Move customers from early routes (SR1/LR1) to later routes for more prep time.</p>
              {shiftable.length === 0 ? <p className="text-sm text-[#999] text-center py-4">No customers available to shift.</p> : shiftable.map((c) => (
                <div key={c.customer_id} data-testid={`shiftable-${c.customer_id}`} className="bg-[#f9f9f9] border border-[#e0e0e0] rounded-xl p-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="font-bold text-[#333]">{c.customer_name}</span>
                    <span className="bg-[#e65100] text-white text-[11px] font-bold px-2 py-0.5 rounded">{c.current_route}</span>
                    <span className="text-xs text-[#777]">{c.item_count} items</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[#555]">Shift to:</span>
                    {c.shift_to_options.map((o) => (
                      <button key={o} onClick={() => setSel({ ...sel, [c.customer_id]: o })}
                        className={`px-3 py-1.5 rounded-md border-2 border-[#1565c0] text-sm font-semibold ${sel[c.customer_id] === o ? "bg-[#1565c0] text-white" : "text-[#1565c0]"}`}>{o}</button>
                    ))}
                    <button onClick={() => shift(c)} disabled={!sel[c.customer_id]} data-testid={`shift-btn-${c.customer_id}`}
                      className={`ml-auto px-4 py-2 rounded-md text-white font-bold text-sm ${sel[c.customer_id] ? "bg-[#4CAF50]" : "bg-[#bbb]"}`}>Shift</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
